import { prodBackend } from '@vromo/api';
import { Graph } from '@vromo/api/graph';

import Tracking from '@vromo/analytics';
import { useSocket } from '@vromo/websockets';
import React, { FC, useEffect, useState } from 'react';
import { match } from 'react-router';

import { ThemeProvider } from '@emotion/react';
import { when } from 'mobx';
import queryString from 'query-string';
import TrackingLinkStore from './TrackingLinkStore';

import { Provider, useStore } from '@vromo/mobx-react';
import { observer } from 'mobx-react-lite';

import { BrandColors } from '@vromo/models';
import { serialize } from 'serializr';
import { isRacJob } from './RAC/helpers';
import favicon from './favicon.png';
import ReactGA from 'react-ga4';

const TRACKING_ID = window.location.host.includes('staging') ? 'G-XMXVF8J0ET' : 'G-RVR966CW48';

/** customized component for 'Royal Automobile Club of Western Australia' */
const RAC = React.lazy(() => import(/* webpackPrefetch: true */ './RAC/RAC.lazy'));
/** standard tracking link component' */
const WeDispatch = React.lazy(
    () => import(/* webpackPrefetch: true */ './WeDispatch/WeDispatch.lazy')
);

// google analytics script
declare global {
    // todo: explain why this is required
    const ga: any;
    interface Window {
        ga: any;
    }
}

interface TrackingLinkProps {
    auth: string;
    jobId: number | undefined;
    host: string;
}

const TrackingLink = observer<TrackingLinkProps>(function TrackingLink({ auth, jobId, host }) {
    const rootStore = useStore<TrackingLinkStore>('rootStore');
    const socketUrl = new Graph(host).root.track(auth, jobId).socketUrl();
    useSocket(socketUrl, rootStore, null);
    // will always contain 1 job and 1 vehicle, unless errors
    const { jobs, vehicles } = rootStore;

    useEffect(() => {
        ReactGA.initialize(TRACKING_ID);
    }, []);

    useEffect(() => {
        when(
            () => vehicles.length !== 0,
            () => {
                const vehicle = vehicles.first()!;
                Tracking.addEventContext({
                    vehicle: vehicle.id,
                    driver: vehicle.driver?.contact?.name
                });
            }
        );

        when(
            () => jobs.length !== 0,
            () => {
                const job = jobs.first()!;
                Tracking.addEventContext({
                    job: job.id,
                    orderNo: job.name,
                    group: job.group,
                    campaign: job.campaign?.id,
                    site: job.site?.id,
                    policy: job.policy?.id
                });

                Tracking.track('Tracking Link Opened', {
                    mapbox: job.campaign?.theme?.map.length === 0,
                    rac: isRacJob(job)
                });

                if (window.ga && job.campaign?.analytics?.google) {
                    window.ga(() => {
                        // TODO: is this ok with or without 'window'
                        ga('create', job.campaign?.analytics?.google, 'auto');
                    });
                }

                if (isRacJob(job)) {
                    setTimeout(() => {
                        if (window.ga) {
                            try {
                                const tracker = window.ga.getAll()[0];

                                if (tracker) {
                                    tracker.set('page', '/rac');
                                    tracker.send('pageview');
                                }
                            } catch (e) {
                                console.error('could not configure GA for Rac');
                            }
                        }
                    }, 15000);
                }
            }
        );
    }, []);

    if (!jobs.length) return null;

    const job = jobs.first()!;

    return (
        <ThemeProvider theme={job.campaign?.theme || serialize(new BrandColors())}>
            {isRacJob(job) ? (
                <RAC auth={auth} host={host} />
            ) : (
                <WeDispatch auth={auth} host={host} />
            )}
        </ThemeProvider>
    );
});

const TrackingLinkContainer: FC<{
    match: match<{ auth: string; job: string }>;
    location: Location;
}> = ({ match, location }) => {
    const [store] = useState(new TrackingLinkStore());
    const { job: jobId, auth } = match.params;

    const possibleHost = queryString.parse(location.search).host || prodBackend;
    const host = (
        /^.+\.(spatula\.io|vromo\.io|fleeteng\.com\.au|trackmyorderlive\.co)$/.test(
            possibleHost.toString()
        )
            ? possibleHost
            : prodBackend
    ) as string;

    const newDomain = 'trackmyorderlive.co';
    const isVromoHost = !host.includes(newDomain);
    const filteredHost = host.replace(newDomain, 'vromo.io').toString();

    useEffect(() => {
        if (isVromoHost) return;

        const title = 'Tracking Link | Connecting customers and brands with every delivery';
        const href = favicon;

        let link = (document.querySelector('link[rel="shortcut icon"]') ||
            document.querySelector('link[rel="icon"]')) as HTMLLinkElement;

        if (!link) {
            link = document.createElement('link');
            link.id = 'favicon';
            link.rel = 'shortcut icon';
            document.head.appendChild(link);
        }

        link.href = href;
        document.title = title;
    }, [isVromoHost]);

    return (
        <Provider rootStore={store}>
            <TrackingLink jobId={parseInt(jobId) ?? undefined} auth={auth} host={filteredHost} />
        </Provider>
    );
};

export default TrackingLinkContainer;
